// Shared client-side CSV + Excel (HTML-table .xls) exporters for tabular reports.
// Column shape matches exportToPdf: [{ header, dataKey }]. Rows are flat objects
// keyed by dataKey. Pairs with utils/exportToPdf.js so a report can offer PDF, CSV,
// and Excel from the same column/row definition (REQ-08 consistent export formats).

function triggerDownload(filename, mime, content) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function exportToCsv(filename, columns, rows) {
  const header = columns.map((c) => csvCell(c.header)).join(',');
  const body = (rows || []).map((r) => columns.map((c) => csvCell(r[c.dataKey])).join(',')).join('\r\n');
  const name = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  // BOM so Excel opens UTF-8 correctly.
  triggerDownload(name, 'text/csv;charset=utf-8', `﻿${header}\r\n${body}`);
}

const xlEsc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function exportToExcel(filename, columns, rows, sheetTitle) {
  const th = columns.map((c) => `<th style="background:#1e3a5f;color:#fff;padding:5px 9px;text-align:left;border:1px solid #16324f">${xlEsc(c.header)}</th>`).join('');
  const trs = (rows || []).map((r) =>
    `<tr>${columns.map((c) => `<td style="padding:4px 9px;border:1px solid #d0d7de">${xlEsc(r[c.dataKey])}</td>`).join('')}</tr>`
  ).join('');
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"><title>${xlEsc(sheetTitle || 'Report')}</title></head><body><table border="1"><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table></body></html>`;
  const name = filename.endsWith('.xls') ? filename : `${filename}.xls`;
  triggerDownload(name, 'application/vnd.ms-excel', html);
}

// Multi-section variants — sections match exportToPdf's shape:
// [{ heading?, columns:[{header,dataKey}], rows }]. Used for combined reports
// (e.g. GM Daily Flash: KPIs + room stats + revenue + guest movement).
export function exportSectionsToCsv(filename, sections) {
  const blocks = (sections || []).map((s) => {
    const head = s.heading ? `${csvCell(s.heading)}\r\n` : '';
    const header = s.columns.map((c) => csvCell(c.header)).join(',');
    const body = (s.rows || []).map((r) => s.columns.map((c) => csvCell(r[c.dataKey])).join(',')).join('\r\n');
    return `${head}${header}\r\n${body}`;
  });
  const name = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  triggerDownload(name, 'text/csv;charset=utf-8', `﻿${blocks.join('\r\n\r\n')}`);
}

export function exportSectionsToExcel(filename, sections, title) {
  const tables = (sections || []).map((s) => {
    const cap = s.heading ? `<tr><td colspan="${s.columns.length}" style="font-weight:bold;background:#eef2f6;padding:6px 9px;border:1px solid #d0d7de">${xlEsc(s.heading)}</td></tr>` : '';
    const th = s.columns.map((c) => `<th style="background:#1e3a5f;color:#fff;padding:5px 9px;text-align:left;border:1px solid #16324f">${xlEsc(c.header)}</th>`).join('');
    const trs = (s.rows || []).map((r) => `<tr>${s.columns.map((c) => `<td style="padding:4px 9px;border:1px solid #d0d7de">${xlEsc(r[c.dataKey])}</td>`).join('')}</tr>`).join('');
    return `<table border="1">${cap}<thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table><br/>`;
  }).join('');
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"><title>${xlEsc(title || 'Report')}</title></head><body>${tables}</body></html>`;
  const name = filename.endsWith('.xls') ? filename : `${filename}.xls`;
  triggerDownload(name, 'application/vnd.ms-excel', html);
}
